import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import API_BASE_URL from '../../config/api';
import { isAdminOrSuper, isSuperAdmin } from '../../utils/roleHelpers';

// Helper function to generate pagination page numbers with ellipsis
const getPaginationPages = (currentPage: number, totalPages: number, maxVisible: number = 7): (number | string)[] => {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  const halfVisible = Math.floor(maxVisible / 2);

  if (currentPage <= halfVisible + 1) {
    for (let i = 1; i <= maxVisible - 2; i++) {
      pages.push(i);
    }
    pages.push('ellipsis');
    pages.push(totalPages);
  } else if (currentPage >= totalPages - halfVisible) {
    pages.push(1);
    pages.push('ellipsis');
    for (let i = totalPages - (maxVisible - 3); i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);
    pages.push('ellipsis');
    for (let i = currentPage - 1; i <= currentPage + 1; i++) {
      pages.push(i);
    }
    pages.push('ellipsis');
    pages.push(totalPages);
  }

  return pages;
};

export default function UserManagement() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showUpdatePassword, setShowUpdatePassword] = useState(false);
  const [currentAdminCompanyId, setCurrentAdminCompanyId] = useState<string>('');
  const [companyIds, setCompanyIds] = useState<string[]>([]);
  const [loadingCompanyIds, setLoadingCompanyIds] = useState(false);
  const [newUser, setNewUser] = useState({
    userid: '',
    company_id: '',
    role: 'user',
    password: '',
  });
  const [updatePassword, setUpdatePassword] = useState({
    userid: '',
    company_id: '',
    password: '',
  });
  const [companyUsers, setCompanyUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [passwordCompanyUsers, setPasswordCompanyUsers] = useState<any[]>([]);
  const [loadingPasswordUsers, setLoadingPasswordUsers] = useState(false);
  const [currentUsername, setCurrentUsername] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const usersPerPage = 10;
  
  // State for super admin all users section
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [loadingAllUsers, setLoadingAllUsers] = useState(false);
  const [allUsersPage, setAllUsersPage] = useState<number>(1);
  const [allUsersTotal, setAllUsersTotal] = useState<number>(0);
  const [allUsersTotalPages, setAllUsersTotalPages] = useState<number>(0);
  const [allUsersFilters, setAllUsersFilters] = useState({
    company_id: '',
    role: '',
  });

  // Check if user has access
  if (!isAdminOrSuper()) {
    navigate('/home');
    return null;
  }

  const canCreateAdmin = isSuperAdmin();
  const isSuper = isSuperAdmin();

  // Load current admin's company_id and username from token
  useEffect(() => {
    const loadCurrentAdminCompanyId = () => {
      try {
        const token = localStorage.getItem('token') || '';
        if (token && token.startsWith('token-')) {
          const tokenParts = token.substring(6).split('-'); // Remove 'token-' prefix and split
          if (tokenParts.length >= 2) {
            // First part is username
            const extractedUsername = tokenParts[0];
            setCurrentUsername(extractedUsername);
            // Company ID is everything after the username
            const extractedCompanyId = tokenParts.slice(1).join('-'); // Join in case company_id contains hyphens
            setCurrentAdminCompanyId(extractedCompanyId);
            // For regular admins, automatically set company_id
            if (!isSuper && extractedCompanyId) {
              setNewUser(prev => ({ ...prev, company_id: extractedCompanyId }));
              setUpdatePassword(prev => ({ ...prev, company_id: extractedCompanyId }));
            }
            return;
          }
        }
        
        // Fallback: try to get from API if token doesn't have company_id
        const loadFromAPI = async () => {
          try {
            const response = await fetch(`${API_BASE_URL}/user`, {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });
            const data = await response.json();
            if (data.success) {
              if (data.username) {
                setCurrentUsername(data.username);
              }
              if (data.company_id) {
                setCurrentAdminCompanyId(data.company_id);
                // For regular admins, automatically set company_id
                if (!isSuper && data.company_id) {
                  setNewUser(prev => ({ ...prev, company_id: data.company_id }));
                  setUpdatePassword(prev => ({ ...prev, company_id: data.company_id }));
                }
              }
            }
          } catch (error) {
            console.error('Failed to load company ID from API:', error);
          }
        };
        loadFromAPI();
      } catch (error) {
        console.error('Failed to extract company ID from token:', error);
      }
    };
    loadCurrentAdminCompanyId();
  }, [isSuper]);

  // Load company IDs for super_admin
  useEffect(() => {
    if (isSuper) {
      const loadCompanyIds = async () => {
        setLoadingCompanyIds(true);
        try {
          const response = await fetch(`${API_BASE_URL}/admin/company-ids`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
            },
          });
          
          if (!response.ok) {
            console.error(`HTTP error! status: ${response.status}`);
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            console.error('Error response:', errorData);
            setCompanyIds([]);
            return;
          }
          
          const data = await response.json();
          console.log('Company IDs response:', data); // Debug log
          if (data.success && Array.isArray(data.company_ids)) {
            console.log('Loaded company IDs:', data.company_ids); // Debug log
            setCompanyIds(data.company_ids);
          } else {
            console.error('Failed to load company IDs:', data.error || data);
            setCompanyIds([]);
          }
        } catch (error) {
          console.error('Failed to load company IDs:', error);
          setCompanyIds([]);
        } finally {
          setLoadingCompanyIds(false);
        }
      };
      loadCompanyIds();
    }
  }, [isSuper]);

  // Function to load users for admin's company_id
  const loadCompanyUsers = async () => {
    if (!currentAdminCompanyId) return;
    
    setLoadingUsers(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/by-company/${encodeURIComponent(currentAdminCompanyId)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      
      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}`);
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Error response:', errorData);
        setCompanyUsers([]);
        return;
      }
      
      const data = await response.json();
      if (data.success && Array.isArray(data.users)) {
        setCompanyUsers(data.users);
        // Reset to first page when users are loaded
        setCurrentPage(1);
      } else {
        console.error('Failed to load users:', data.error || data);
        setCompanyUsers([]);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
      setCompanyUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Load users for admin's company_id (only for admin role, not super_admin)
  useEffect(() => {
    if (!isSuper && currentAdminCompanyId) {
      loadCompanyUsers();
    } else if (isSuper) {
      // For super_admin, clear users list and load all users
      setCompanyUsers([]);
      loadAllUsers(1);
    }
  }, [isSuper, currentAdminCompanyId]);

  // Load all users function for super admin
  const loadAllUsers = async (page: number = allUsersPage) => {
    setLoadingAllUsers(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', usersPerPage.toString());
      
      if (allUsersFilters.company_id) {
        params.append('company_id', allUsersFilters.company_id);
      }
      if (allUsersFilters.role) {
        params.append('role', allUsersFilters.role);
      }
      
      const response = await fetch(`${API_BASE_URL}/admin/users?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      
      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}`);
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Error response:', errorData);
        setAllUsers([]);
        setAllUsersTotal(0);
        setAllUsersTotalPages(0);
        return;
      }
      
      const data = await response.json();
      if (data.success && Array.isArray(data.users)) {
        setAllUsers(data.users);
        setAllUsersTotal(data.total || 0);
        setAllUsersTotalPages(data.total_pages || 0);
        setAllUsersPage(data.page || page);
      } else {
        console.error('Failed to load all users:', data.error || data);
        setAllUsers([]);
        setAllUsersTotal(0);
        setAllUsersTotalPages(0);
      }
    } catch (error) {
      console.error('Failed to load all users:', error);
      setAllUsers([]);
      setAllUsersTotal(0);
      setAllUsersTotalPages(0);
    } finally {
      setLoadingAllUsers(false);
    }
  };

  // Function to load users for a specific company (used by super admin in Update Password section)
  const loadUsersForPasswordCompany = async (companyId: string) => {
    if (!companyId) {
      setPasswordCompanyUsers([]);
      return;
    }
    setLoadingPasswordUsers(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/by-company/${encodeURIComponent(companyId)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });

      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}`);
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Error response (password company users):', errorData);
        setPasswordCompanyUsers([]);
        return;
      }

      const data = await response.json();
      if (data.success && Array.isArray(data.users)) {
        setPasswordCompanyUsers(data.users);
      } else {
        console.error('Failed to load users for password company:', data.error || data);
        setPasswordCompanyUsers([]);
      }
    } catch (error) {
      console.error('Failed to load users for password company:', error);
      setPasswordCompanyUsers([]);
    } finally {
      setLoadingPasswordUsers(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.userid || !newUser.company_id || !newUser.password) {
      alert('Please fill in User ID, Company ID, and Password');
      return;
    }

    if (newUser.role === 'admin' && !canCreateAdmin) {
      alert('Only super admin can create admin users');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          userid: newUser.userid,
          company_id: newUser.company_id,
          password: newUser.password,
          role: newUser.role,
          pdfclientname: 'N/A',
          jobtype: 'N/A',
        }),
      });

      const result = await response.json();
      if (response.ok && result.success) {
        alert(`${newUser.role === 'admin' ? 'Admin' : 'User'} created successfully!`);
        // Reset form
        setNewUser({
          userid: '',
          company_id: isSuper ? '' : currentAdminCompanyId, // Keep company_id for regular admins
          role: 'user',
          password: '',
        });
        // Refresh users list for admin role
        if (!isSuper && currentAdminCompanyId) {
          loadCompanyUsers();
        }
      } else {
        if (result.error === 'User already exists in this company') {
          alert('Error: A user with this username already exists in the selected company.');
        } else if (result.error && result.error.includes('super_admin')) {
          alert('Error: Only super admin can create admin users.');
        } else {
          alert(`Error creating ${newUser.role === 'admin' ? 'admin' : 'user'}: ${result.error || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('Network error:', error);
      alert('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!updatePassword.userid || !updatePassword.company_id || !updatePassword.password) {
      alert('Please fill in User ID, Company ID, and New Password');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${encodeURIComponent(updatePassword.userid)}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          password: updatePassword.password,
          company_id: updatePassword.company_id,
        }),
      });

      const result = await response.json();
      if (response.ok && result.success) {
        alert('Password updated successfully!');
        setUpdatePassword({
          userid: '',
          company_id: isSuper ? '' : currentAdminCompanyId, // Keep company_id for regular admins
          password: '',
        });
        setShowUpdatePassword(false);
      } else {
        alert(`Error updating password: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Network error:', error);
      alert('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (userid: string) => {
    if (!currentAdminCompanyId) {
      alert('Company ID is required');
      return;
    }

    // Prevent admin from deleting themselves
    if (userid === currentUsername) {
      alert('You cannot delete your own account. Please contact a super admin if you need to delete your account.');
      return;
    }

    // Confirmation dialog
    const confirmMessage = `Are you sure you want to delete user "${userid}"?\n\nThis will permanently delete:\n- The user account\n- All parsers created by this user\n\nThis action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/users/${encodeURIComponent(userid)}?company_id=${encodeURIComponent(currentAdminCompanyId)}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
          },
        }
      );

      const result = await response.json();
      if (response.ok && result.success) {
        alert(`User "${userid}" and all their parsers have been deleted successfully!`);
        // Refresh users list
        loadCompanyUsers();
      } else {
        alert(`Error deleting user: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Network error:', error);
      alert('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
      <Navbar showUserActions={true} />
      
      <div className="flex flex-col items-center px-4 py-8">
        <div className="w-full max-w-4xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-800 mb-2">User Management</h1>
              <div className="w-full h-px bg-gray-300"></div>
            </div>
            <Button variant="outline" onClick={() => navigate('/home')}>
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Home
            </Button>
          </div>

          {/* Create User/Admin Section */}
          <Card className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Create {newUser.role === 'admin' ? 'Admin' : 'User'}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                {isSuper ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company ID *
                    </label>
                    <select
                      value={newUser.company_id}
                      onChange={(e) => setNewUser({ ...newUser, company_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={loadingCompanyIds}
                    >
                      <option value="">Select Company ID</option>
                      {companyIds.length > 0 ? (
                        companyIds.map((cid) => (
                          <option key={cid} value={cid}>
                            {cid.toUpperCase()}
                          </option>
                        ))
                      ) : (
                        !loadingCompanyIds && <option value="" disabled>No company IDs found</option>
                      )}
                    </select>
                    {loadingCompanyIds && (
                      <p className="text-xs text-gray-500 mt-1">
                        <i className="ri-loader-line animate-spin mr-1"></i>
                        Loading company IDs...
                      </p>
                    )}
                    {!loadingCompanyIds && companyIds.length === 0 && (
                      <p className="text-xs text-yellow-600 mt-1">
                        <i className="ri-alert-line mr-1"></i>
                        No company IDs found. Create an admin first to add a company ID.
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    <Input
                      type="text"
                      placeholder="Company ID"
                      value={newUser.company_id}
                      onChange={(e) => setNewUser({ ...newUser, company_id: e.target.value })}
                      label="Company ID *"
                      readOnly={!isSuper && currentAdminCompanyId !== ''}
                    />
                    {!isSuper && currentAdminCompanyId !== '' && (
                      <p className="text-xs text-gray-500 mt-1">Company ID is set to your company</p>
                    )}
                  </div>
                )}
              </div>
              <Input
                type="text"
                placeholder="Username"
                value={newUser.userid}
                onChange={(e) => setNewUser({ ...newUser, userid: e.target.value })}
                label="Username *"
              />
              {canCreateAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              )}
              <Input
                type="password"
                placeholder="Password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                label="Password *"
              />
            </div>
            <div className="flex justify-end space-x-4 mt-6">
              <Button 
                onClick={handleCreateUser} 
                disabled={isLoading}
                className="whitespace-nowrap"
              >
                {isLoading ? (
                  <>
                    <i className="ri-loader-line animate-spin mr-2"></i>
                    Creating...
                  </>
                ) : (
                  `Create ${newUser.role === 'admin' ? 'Admin' : 'User'}`
                )}
              </Button>
            </div>
          </Card>

          {/* Update Password Section */}
          <Card>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold text-gray-800">Update Password</h2>
              <Button
                variant="outline"
                onClick={() => {
                  setShowUpdatePassword(!showUpdatePassword);
                  if (showUpdatePassword) {
                    setUpdatePassword({ 
                      userid: '', 
                      company_id: isSuper ? '' : currentAdminCompanyId, 
                      password: '' 
                    });
                  }
                }}
              >
                {showUpdatePassword ? (
                  <>
                    <i className="ri-close-line mr-2"></i>
                    Cancel
                  </>
                ) : (
                  <>
                    <i className="ri-lock-password-line mr-2"></i>
                    Update Password
                  </>
                )}
              </Button>
            </div>

            {showUpdatePassword && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {isSuper ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      User ID *
                    </label>
                    <select
                      value={updatePassword.userid}
                      onChange={(e) => setUpdatePassword({ ...updatePassword, userid: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={loadingPasswordUsers || !updatePassword.company_id}
                    >
                      <option value="">{updatePassword.company_id ? 'Select User' : 'Select a company first'}</option>
                      {passwordCompanyUsers.length > 0 ? (
                        passwordCompanyUsers.map((user) => (
                          <option key={user.id || user.userid} value={user.userid}>
                            {user.userid} {user.role === 'admin' ? '(Admin)' : ''}
                          </option>
                        ))
                      ) : (
                        !loadingPasswordUsers &&
                        updatePassword.company_id && <option value="" disabled>No users found for this company</option>
                      )}
                    </select>
                    {loadingPasswordUsers && (
                      <p className="text-xs text-gray-500 mt-1">
                        <i className="ri-loader-line animate-spin mr-1"></i>
                        Loading users...
                      </p>
                    )}
                    {!loadingPasswordUsers && updatePassword.company_id && passwordCompanyUsers.length === 0 && (
                      <p className="text-xs text-yellow-600 mt-1">
                        <i className="ri-alert-line mr-1"></i>
                        No users found for the selected company.
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      User ID *
                    </label>
                    <select
                      value={updatePassword.userid}
                      onChange={(e) => setUpdatePassword({ ...updatePassword, userid: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={loadingUsers}
                    >
                      <option value="">Select User</option>
                      {companyUsers.length > 0 ? (
                        companyUsers.map((user) => (
                          <option key={user.id || user.userid} value={user.userid}>
                            {user.userid} {user.role === 'admin' ? '(Admin)' : ''}
                          </option>
                        ))
                      ) : (
                        !loadingUsers && <option value="" disabled>No users found</option>
                      )}
                    </select>
                    {loadingUsers && (
                      <p className="text-xs text-gray-500 mt-1">
                        <i className="ri-loader-line animate-spin mr-1"></i>
                        Loading users...
                      </p>
                    )}
                    {!loadingUsers && companyUsers.length === 0 && (
                      <p className="text-xs text-yellow-600 mt-1">
                        <i className="ri-alert-line mr-1"></i>
                        No users found for this company.
                      </p>
                    )}
                  </div>
                )}
                <div>
                  {isSuper ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company ID *
                      </label>
                      <select
                        value={updatePassword.company_id}
                        onChange={(e) => {
                          const newCompanyId = e.target.value;
                          setUpdatePassword({ ...updatePassword, company_id: newCompanyId, userid: '' });
                          loadUsersForPasswordCompany(newCompanyId);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        disabled={loadingCompanyIds}
                      >
                        <option value="">Select Company ID</option>
                        {companyIds.length > 0 ? (
                          companyIds.map((cid) => (
                            <option key={cid} value={cid}>
                              {cid.toUpperCase()}
                            </option>
                          ))
                        ) : (
                          !loadingCompanyIds && <option value="" disabled>No company IDs found</option>
                        )}
                      </select>
                      {!loadingCompanyIds && companyIds.length === 0 && (
                        <p className="text-xs text-yellow-600 mt-1">
                          <i className="ri-alert-line mr-1"></i>
                          No company IDs found. Create an admin first to add a company ID.
                        </p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <Input
                        type="text"
                        placeholder="Company ID"
                        value={updatePassword.company_id}
                        onChange={(e) => setUpdatePassword({ ...updatePassword, company_id: e.target.value })}
                        label="Company ID *"
                        readOnly={!isSuper && currentAdminCompanyId !== ''}
                      />
                      {!isSuper && currentAdminCompanyId !== '' && (
                        <p className="text-xs text-gray-500 mt-1">Company ID is set to your company</p>
                      )}
                    </div>
                  )}
                </div>
                <Input
                  type="password"
                  placeholder="New Password"
                  value={updatePassword.password}
                  onChange={(e) => setUpdatePassword({ ...updatePassword, password: e.target.value })}
                  label="New Password *"
                />
                <div className="flex items-end">
                  <Button 
                    onClick={handleUpdatePassword} 
                    disabled={isLoading}
                    className="w-full"
                  >
                    {isLoading ? (
                      <>
                        <i className="ri-loader-line animate-spin mr-2"></i>
                        Updating...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line mr-2"></i>
                        Update Password
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* All Users Section - Only for super admin */}
          {isSuper && (
            <Card className="mt-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold text-gray-800">All Users</h2>
                <Button
                  variant="outline"
                  onClick={() => {
                    setAllUsersPage(1);
                    loadAllUsers(1);
                  }}
                  disabled={loadingAllUsers}
                  className="flex items-center"
                >
                  <i className={`ri-refresh-line mr-2 ${loadingAllUsers ? 'animate-spin' : ''}`}></i>
                  Refresh
                </Button>
              </div>

              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company ID
                  </label>
                  <select
                    value={allUsersFilters.company_id}
                    onChange={(e) => {
                      setAllUsersFilters({ ...allUsersFilters, company_id: e.target.value });
                      setAllUsersPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">All Companies</option>
                    {companyIds.map((cid) => (
                      <option key={cid} value={cid}>
                        {cid.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={allUsersFilters.role}
                    onChange={(e) => {
                      setAllUsersFilters({ ...allUsersFilters, role: e.target.value });
                      setAllUsersPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">All Roles</option>
                    <option value="super_admin">Super Admin</option>
                    <option value="admin">Admin</option>
                    <option value="user">User</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={() => {
                      setAllUsersPage(1);
                      loadAllUsers(1);
                    }}
                    disabled={loadingAllUsers}
                    className="w-full"
                  >
                    {loadingAllUsers ? (
                      <>
                        <i className="ri-loader-line animate-spin mr-2"></i>
                        Loading...
                      </>
                    ) : (
                      <>
                        <i className="ri-search-line mr-2"></i>
                        Apply Filters
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {loadingAllUsers ? (
                <div className="flex justify-center items-center py-8">
                  <i className="ri-loader-line animate-spin text-2xl text-gray-500 mr-2"></i>
                  <span className="text-gray-600">Loading users...</span>
                </div>
              ) : allUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <i className="ri-user-line text-4xl mb-2"></i>
                  <p>No users found.</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100 border-b-2 border-gray-300">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 align-middle">Username</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 align-middle">Company ID</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 align-middle">Role</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 align-middle">Status</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 align-middle w-20">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allUsers.map((user, index) => (
                          <tr 
                            key={user.id || index} 
                            className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-4 py-3 text-sm text-gray-800 font-medium align-middle">{user.userid}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 align-middle">{user.company_id ? user.company_id.toUpperCase() : '-'}</td>
                            <td className="px-4 py-3 text-sm align-middle">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                user.role === 'admin' 
                                  ? 'bg-purple-100 text-purple-800' 
                                  : user.role === 'super_admin'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-blue-100 text-blue-800'
                              }`}>
                                {user.role === 'super_admin' ? 'Super Admin' : user.role === 'admin' ? 'Admin' : 'User'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm align-middle">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                user.status === 'active' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {user.status === 'active' ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm align-middle text-center">
                              {user.userid === currentUsername ? (
                                <span className="text-gray-400 text-xs italic">-</span>
                              ) : (
                                <button
                                  onClick={() => handleDeleteUser(user.userid)}
                                  disabled={isLoading}
                                  className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Delete user"
                                >
                                  <i className="ri-delete-bin-line text-lg"></i>
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination Controls */}
                  {allUsersTotal > 0 && (
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                      <div className="text-sm text-gray-600">
                        Showing {(allUsersPage - 1) * usersPerPage + 1} to {Math.min(allUsersPage * usersPerPage, allUsersTotal)} of {allUsersTotal} users
                      </div>
                      {allUsersTotalPages > 1 ? (
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            onClick={() => {
                              const newPage = Math.max(1, allUsersPage - 1);
                              setAllUsersPage(newPage);
                              loadAllUsers(newPage);
                            }}
                            disabled={allUsersPage === 1 || loadingAllUsers}
                            className="px-3 py-1.5"
                          >
                            <i className="ri-arrow-left-line"></i>
                          </Button>
                          <div className="flex items-center space-x-1">
                            {Array.from({ length: allUsersTotalPages }, (_, i) => i + 1).map((page) => (
                              <button
                                key={page}
                                onClick={() => {
                                  setAllUsersPage(page);
                                  loadAllUsers(page);
                                }}
                                disabled={loadingAllUsers}
                                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                                  allUsersPage === page
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                              >
                                {page}
                              </button>
                            ))}
                          </div>
                          <Button
                            variant="outline"
                            onClick={() => {
                              const newPage = Math.min(allUsersTotalPages, allUsersPage + 1);
                              setAllUsersPage(newPage);
                              loadAllUsers(newPage);
                            }}
                            disabled={allUsersPage === allUsersTotalPages || loadingAllUsers}
                            className="px-3 py-1.5"
                          >
                            <i className="ri-arrow-right-line"></i>
                          </Button>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">
                          Page 1 of 1
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </Card>
          )}

          {/* Company Users List Section - Only for admin role */}
          {!isSuper && currentAdminCompanyId && (
            <Card className="mt-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold text-gray-800">
                  Users in Company: {currentAdminCompanyId.toUpperCase()}
                </h2>
                <Button
                  variant="outline"
                  onClick={loadCompanyUsers}
                  disabled={loadingUsers}
                  className="flex items-center"
                >
                  <i className={`ri-refresh-line mr-2 ${loadingUsers ? 'animate-spin' : ''}`}></i>
                  Refresh
                </Button>
              </div>

              {loadingUsers ? (
                <div className="flex justify-center items-center py-8">
                  <i className="ri-loader-line animate-spin text-2xl text-gray-500 mr-2"></i>
                  <span className="text-gray-600">Loading users...</span>
                </div>
              ) : companyUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <i className="ri-user-line text-4xl mb-2"></i>
                  <p>No users found for this company.</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100 border-b-2 border-gray-300">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 align-middle">Username</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 align-middle">Role</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 align-middle">Status</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 align-middle w-20">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companyUsers
                          .slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage)
                          .map((user, index) => (
                            <tr 
                              key={user.id || index} 
                              className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                            >
                              <td className="px-4 py-3 text-sm text-gray-800 font-medium align-middle">{user.userid}</td>
                              <td className="px-4 py-3 text-sm align-middle">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  user.role === 'admin' 
                                    ? 'bg-purple-100 text-purple-800' 
                                    : user.role === 'super_admin'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {user.role === 'super_admin' ? 'Super Admin' : user.role === 'admin' ? 'Admin' : 'User'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm align-middle">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  user.status === 'active' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {user.status === 'active' ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm align-middle text-center">
                                {user.userid === currentUsername ? (
                                  <span className="text-gray-400 text-xs italic">-</span>
                                ) : (
                                  <button
                                    onClick={() => handleDeleteUser(user.userid)}
                                    disabled={isLoading}
                                    className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Delete user"
                                  >
                                    <i className="ri-delete-bin-line text-lg"></i>
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination Controls */}
                  {Math.ceil(companyUsers.length / usersPerPage) > 1 && (
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                      <div className="text-sm text-gray-600">
                        Showing {(currentPage - 1) * usersPerPage + 1} to {Math.min(currentPage * usersPerPage, companyUsers.length)} of {companyUsers.length} users
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1 || loadingUsers}
                          className="px-3 py-1.5"
                        >
                          <i className="ri-arrow-left-line"></i>
                        </Button>
                        <div className="flex items-center space-x-1">
                          {getPaginationPages(currentPage, Math.ceil(companyUsers.length / usersPerPage)).map((page, idx) => {
                            if (page === 'ellipsis') {
                              return (
                                <span key={`ellipsis-${idx}`} className="px-2 py-1.5 text-sm text-gray-500">
                                  ...
                                </span>
                              );
                            }
                            const pageNum = page as number;
                            return (
                              <button
                                key={pageNum}
                                onClick={() => setCurrentPage(pageNum)}
                                disabled={loadingUsers}
                                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                                  currentPage === pageNum
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>
                        <Button
                          variant="outline"
                          onClick={() => setCurrentPage(prev => Math.min(Math.ceil(companyUsers.length / usersPerPage), prev + 1))}
                          disabled={currentPage === Math.ceil(companyUsers.length / usersPerPage) || loadingUsers}
                          className="px-3 py-1.5"
                        >
                          <i className="ri-arrow-right-line"></i>
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}


